import { Box, Stack, type Theme, Typography, useTheme } from "@mui/material";
import reportNoData from "assets/images/nodata/reportNoData.png";
import { Avatar, Tooltip } from "components";
import { useGetBoardWiseTicketStats, useGlobalStore } from "controller";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import type { BoardWiseTicketStats } from "shared";
import { useDashboardScope } from "../../../hooks/useDashboardScope";
import { activityPeriodAtom } from "../../../store";
import type { BoardTicketBreakdown } from "../../../types";
import { ActivityCard } from "./ActivityCard";
import { ActivityEmptyState } from "./ActivityEmptyState";
import { ActivityLegend } from "./ActivityLegend";

type PriorityKey = "critical" | "high" | "medium" | "low";

const PRIORITIES: Array<{ key: PriorityKey; label: string }> = [
  { key: "critical", label: "Critical" },
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" },
];

const getPriorityColor = (theme: Theme, key: PriorityKey) =>
  ({
    critical: theme.palette.primitive.system.red[800],
    high: theme.palette.primitive.system.orange[700],
    medium: theme.palette.primitive.system.yellow[700],
    low: theme.palette.primitive.neutrals.neutral[500],
  })[key];

/** Two-digit padding matches the count column in the design ("09", not "9"). */
const padCount = (count: number) => String(count).padStart(2, "0");

export const TicketsByBoardCard = () => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const period = useAtomValue(activityPeriodAtom);
  const { reportResource, isReady } = useDashboardScope();

  // Held until the scope resolves — an empty `resource` reads as tenant-wide, so
  // firing early would flash numbers for the whole MSP.
  const { data, isFetching } = useGetBoardWiseTicketStats(
    {
      timePeriod: period,
      timezone,
      resource: reportResource,
    },
    isReady,
  );

  const stats = data as BoardWiseTicketStats | undefined;

  // The API identifies boards by name and returns them busiest-first — the order
  // it sends is the order we render. Boards with no tickets are dropped so the
  // list only shows boards with activity in the period.
  const boards = useMemo<BoardTicketBreakdown[]>(() => {
    return (stats?.boards ?? [])
      .map((board) => ({
        id: board.board_name,
        name: board.board_name,
        total: board.total ?? 0,
        critical: board.priority?.critical ?? 0,
        high: board.priority?.high ?? 0,
        medium: board.priority?.medium ?? 0,
        low: board.priority?.low ?? 0,
      }))
      .filter((board) => board.total > 0);
  }, [stats]);

  const total = stats?.total_tickets ?? 0;

  // Bars are scaled against the busiest board so the smaller boards stay
  // readable instead of collapsing into slivers of the tenant total.
  const maxBoardTotal = Math.max(...boards.map((board) => board.total), 1);

  const legendItems = PRIORITIES.map(({ key, label }) => ({
    label,
    color: getPriorityColor(theme, key),
  }));

  const isEmpty = !isFetching && isReady && boards.length === 0;

  return (
    <ActivityCard
      title="Tickets by board"
      // The tenant can have dozens of boards — cap the card at half the viewport
      // and let the board list scroll inside it.
      sx={{ maxHeight: "50vh" }}
    >
      {/* The total and the priority key only describe the rows below, so they
          drop out with them rather than heading an empty card. */}
      {!isEmpty && (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" sx={{ alignItems: "baseline", gap: "6px" }}>
            <Typography variant="h5" sx={{ color: theme.palette.lightBg.high }}>
              {total}
            </Typography>
            <Typography
              variant="caption-3"
              sx={{ color: theme.palette.lightBg.low }}
            >
              {total === 1 ? "ticket" : "tickets"}
            </Typography>
          </Stack>
          <ActivityLegend items={legendItems} />
        </Stack>
      )}

      <Stack sx={{ flex: 1, minHeight: 0, gap: "16px", overflowY: "auto" }}>
        {isEmpty ? (
          <ActivityEmptyState
            image={reportNoData}
            title="No tickets yet"
            description="No ticket activity by board to display."
            imageWidth="110px"
          />
        ) : (
          boards.map((board) => (
            <BoardRow key={board.id} board={board} scale={maxBoardTotal} />
          ))
        )}
      </Stack>
    </ActivityCard>
  );
};

const BoardRow = ({
  board,
  scale,
}: {
  board: BoardTicketBreakdown;
  /** Ticket count the full bar width represents. */
  scale: number;
}) => {
  const theme = useTheme();
  const boardTotal = board.total;

  // The bar rarely spans the full track, so the container's overflow clip only
  // rounds the left end — the trailing segment has to round its own right edge.
  const segments = PRIORITIES.filter(({ key }) => board[key] > 0);

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: "8px" }}>
      <Avatar fullname={board.name} variants="xs" shape="square" tooltip />

      <Typography
        variant="body-xsmall"
        sx={{
          width: "104px",
          flexShrink: 0,
          color: theme.palette.lightBg.low,
        }}
        noWrap
      >
        {board.name}
      </Typography>

      <Stack
        direction="row"
        role="img"
        aria-label={`${board.name}: ${boardTotal} tickets`}
        sx={{
          flex: 1,
          minWidth: 0,
          height: "8px",
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        {segments.map(({ key, label }, index) => (
          <Tooltip
            key={key}
            title={`${label}: ${board[key]}`}
            disableSentenceCase
          >
            <Box
              sx={{
                width: `${(board[key] / scale) * 100}%`,
                backgroundColor: getPriorityColor(theme, key),
                borderTopRightRadius:
                  index === segments.length - 1 ? "2px" : undefined,
                borderBottomRightRadius:
                  index === segments.length - 1 ? "2px" : undefined,
              }}
            />
          </Tooltip>
        ))}
      </Stack>

      <Typography
        variant="body-small"
        sx={{ flexShrink: 0, color: theme.palette.lightBg.medium }}
      >
        {padCount(boardTotal)}
      </Typography>
    </Stack>
  );
};
